import { BinaryType } from "../@types/Binary";
import { Arquitecture } from "./Arquitecture"
import { Downloader } from "./Donwloader";
import * as fs from "fs";
import * as path from "path";
import * as os from "os";
import { fileURLToPath } from "url";
import { dirname } from "path";

class Binary{
    private static isDownloading = false;
    private static downloadPromise: Promise<void> | null = null;
    private static cachedBinaries: { [key: string]: string } = {};

    static async ensureBinariesExist(): Promise<void> {
        if (this.isDownloading && this.downloadPromise) {
            await this.downloadPromise;
            return;
        }

        const binDir = this.getBinDir();

        // Always check and download/update binaries
        if (!this.isDownloading) {
            this.isDownloading = true;
            this.downloadPromise = this.downloadBinaries();

            try {
                await this.downloadPromise;
            } finally {
                this.isDownloading = false;
                this.downloadPromise = null;
            }
        }
    }

    private static async downloadBinaries(): Promise<void> {
        try {
            const binDir = this.getBinDir();
            await Downloader.download(binDir);
        } catch (error) {
            console.warn('⚠️ Dbcube: Error descargando binarios:', (error as Error).message);
            console.log('🔧 Los binarios se intentarán descargar en la próxima ejecución');
        }
    }

    private static getBinDir(): string {
        // Get __dirname equivalent for ESM
        const __filename = typeof import.meta !== 'undefined' && import.meta.url
            ? fileURLToPath(import.meta.url)
            : '';
        const __dirname = __filename ? dirname(__filename) : process.cwd();

        // Try to find a suitable directory for binaries in order of preference
        const possibleDirs = [
            path.resolve(process.cwd(), '.dbcube', 'bin'),
            path.resolve(process.cwd(), 'node_modules', '.dbcube', 'bin'),
            path.resolve(__dirname, '..', 'bin'),
        ];

        // Use the first one that can be created or already exists
        for (const dir of possibleDirs) {
            try {
                if (!fs.existsSync(dir)) {
                    fs.mkdirSync(dir, { recursive: true });
                }
                
                
                return dir;
            } catch {
                // Try next directory
                continue;
            }
        }

        // Fallback to .dbcube directory in temp
        const tempDir = path.join(os.tmpdir(), '.dbcube', 'bin');
        fs.mkdirSync(tempDir, { recursive: true });
        return tempDir;
    }


    private static findVersionedBinary(binDir: string, prefix: string, platform: string, arch: string): string {
        // Check cache first
        const cacheKey = `${prefix}-${platform}-${arch}`;
        if (this.cachedBinaries[cacheKey]) {
            const cachedPath = path.join(binDir, this.cachedBinaries[cacheKey]);
            if (fs.existsSync(cachedPath)) {
                return cachedPath;
            }
        }

        try {
            const files = fs.readdirSync(binDir);
            const extension = platform === 'windows' ? '.exe' : '';
            const pattern = new RegExp(`^${prefix}-engine-v\\d+\\.\\d+\\.\\d+-${platform}-${arch}${extension.replace('.', '\\.')}$`);

            const matchingFile = files.find(f => pattern.test(f));

            if (matchingFile) {
                this.cachedBinaries[cacheKey] = matchingFile;
                return path.join(binDir, matchingFile);
            }
        } catch (error) {
            // Directory doesn't exist or can't read
        }

        // Fallback to non-versioned name (for backwards compatibility)
        const fallbackName = `${prefix}-engine-${platform}-${arch}${platform === 'windows' ? '.exe' : ''}`;
        return path.join(binDir, fallbackName);
    }

    static async get(): Promise<BinaryType> {
        await this.ensureBinariesExist();

        const arch = new Arquitecture();
        const platform = arch.getPlatform();
        const architecture = arch.getArchitecture();
        const binDir = this.getBinDir();

        const platformMap: Record<string, string> = {
            windows: "windows",
            linux: "linux",
            darwin: "macos"
        };

        const archMap: Record<string, string> = {
            x86_64: "x64",
            aarch64: "arm64"
        };

        const plat = platformMap[platform];
        const archSuffix = archMap[architecture];

        if (plat && archSuffix) {
            return {
                query_engine: this.findVersionedBinary(binDir, 'query', plat, archSuffix),
                schema_engine: this.findVersionedBinary(binDir, 'schema', plat, archSuffix)
            };
        }

        return {
            query_engine: "",
            schema_engine: ""
        };
    }
}

export { Binary }